import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Badge,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { authSelector, qrcVerifiedSelector, clearQrcToken } from "../services/redux/slices/authSlice";
import ProfileAvatar from "../components/ui/ProfileAvatar";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CompanyLogo from "../assets/svgIcons/company-logo.svg";
import userd from "../assets/svgIcons/profile.svg";
import logout from "../assets/svgIcons/logout.svg";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { setDocumentDiscardModal } from "../services/redux/slices/documentFormSlice";
import { TruncatedTextWithTooltip } from "../components/ui/TruncatedTextWithTooltip";
import MobileDrawer from "../components/ui/mobile/MobileDrawer";
import { ROUTES } from "../routes/routerConfig";
import { persistor, RootState } from "../services/redux/store";
import ActiveApplicationIcon from "../assets/svgIcons/active-application.svg";
import InActiveApplicationIcon from "../assets/svgIcons/inactive-application.svg";
import ActiveFlexiIcon from "../assets/svgIcons/active-flexi-loan.svg";
import InActiveFlexiIcon from "../assets/svgIcons/inactive-flexi-loan.svg";
import ActiveLoanFlexiIcon from "../assets/svgIcons/active-loan-flexi-loan.svg";
import InActiveLoanFlexiIcon from "../assets/svgIcons/inactive-loan-flexi-loan.svg";
import ActiveWithdrawFlexiIcon from "../assets/svgIcons/active-withdraw-flexi-loan.svg";
import InActiveWithdrawFlexiIcon from "../assets/svgIcons/inactive-withdraw-flexi-loan.svg";
import ActivePaymentsFlexiIcon from "../assets/svgIcons/active-payments-flexi-loan.svg";
import InActivePaymentsFlexiIcon from "../assets/svgIcons/inactive-payments-flexi-loan.svg";
import ActiveDocumentFlexiIcon from "../assets/svgIcons/active-document-flexi-loan.svg";
import InActiveDocumentFlexiIcon from "../assets/svgIcons/inactive-document-flexi-loan.svg";
import ActiveTermLoanIcon from "../assets/svgIcons/active-term-loan.svg";
import InActiveTermLoanIcon from "../assets/svgIcons/inactive-term-loan.svg";
import ActiveDashboardTermIcon from "../assets/svgIcons/active-dashboard-term-loan.svg";
import InActiveDashboardTermIcon from "../assets/svgIcons/inactive-dashboard-term-loan.svg";
import ActiveFundReqTermIcon from "../assets/svgIcons/active-fund-req-term-loan.svg";
import InActiveFundReqTermIcon from "../assets/svgIcons/inactive-fund-req-term-loan.svg";
import ActivePaymentTermIcon from "../assets/svgIcons/active-payment-term-loan.svg";
import InActivePaymentTermIcon from "../assets/svgIcons/inactive-payment-term-loan.svg";
import ActiveDocumentTermIcon from "../assets/svgIcons/active-document-term-loan.svg";
import InActiveDocumentTermIcon from "../assets/svgIcons/inactive-documents-term-loan.svg";
import ActiveHistoryTermIcon from "../assets/svgIcons/active-history-term-loan.svg";
import InActiveHistoryTermIcon from "../assets/svgIcons/inactive-history-term-loan.svg";
import ActiveQuickLinksIcon from "../assets/svgIcons/active-quick-links.svg";
import InActiveQuickLinksIcon from "../assets/svgIcons/inactive-quick-links.svg";
import { setIsBypassLogin } from "../services/redux/slices/stepperDynamicFormSlice";
import authServices from "../services/api/service/authService";

export interface MobileMenuItem {
  title: string;
  path?: string;
  icon?: any;
  subMenus?: MobileMenuItem[];
}
export interface MobileMenuItem {
  title: string;
  path?: string;
  activeIcon?: React.ElementType | string; 
  inActiveIcon?: React.ElementType | string;
  subMenus?: MobileMenuItem[];
}

interface HeaderWelcomeProps {
  notificationCount?: number;
  userName?: string;
  avatarSrc?: string;
  isAuthenticated: boolean;
  onMenuClick?: () => void;
}
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
export const HeaderWelcome = ({
  notificationCount = 0,
  userName = "",
  avatarSrc = "",
  isAuthenticated,
}: HeaderWelcomeProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isProfileMenuOpen = Boolean(anchorEl);
  const { userProfile, selectedFormType } = useSelector(authSelector);
  // A pre-login QRC-verified visitor (Help Center OTP flow) with no normal login session.
  const qrcVerified = useSelector(qrcVerifiedSelector);
  const isQrcOnly = qrcVerified && !isAuthenticated;
  const tabType = useSelector((state: RootState) => state.tabs.tabType);
  const businessType = useSelector((state: RootState) => state?.auth?.businessType?.name);
  const termLoanData = useSelector((state: RootState) => state?.auth?.loanCategory);

  const mobileMenuItems: MobileMenuItem[] = useMemo(() => {
    const INTERNAL_MENUS: MobileMenuItem[] = [
      {
        path: "",
        title: "Application",
        activeIcon: ActiveApplicationIcon,
        inActiveIcon: InActiveApplicationIcon,
        subMenus: [
          // {
          //   path: "/application",
          //   title: "New Applications",
          //   activeIcon: ActiveApplicationIcon,
          //   inActiveIcon: InActiveApplicationIcon,
          // },
          {
            path: "/application/existing-application",
            title: "Existing Applications",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
        ],
      },
      {
        path: "",
        title: "Term Loan",
        activeIcon: ActiveTermLoanIcon,
        inActiveIcon: InActiveTermLoanIcon,
        subMenus: [
          {
            path: "/dashboard",
            title: "Dashboard",
            activeIcon: ActiveDashboardTermIcon,
            inActiveIcon: InActiveDashboardTermIcon,
          },
          {
            path: "",
            title: "Disbursement",
            activeIcon: ActiveFundReqTermIcon,
            inActiveIcon: InActiveFundReqTermIcon,
          },
          {
            path: "",
            title: "Payment",
            activeIcon: ActivePaymentTermIcon,
            inActiveIcon: InActivePaymentTermIcon,
          },
          {
            path: "",
            title: "Documents",
            activeIcon: ActiveDocumentTermIcon,
            inActiveIcon: InActiveDocumentTermIcon,
          },
          {
            path: "",
            title: "History",
            activeIcon: ActiveHistoryTermIcon,
            inActiveIcon: InActiveHistoryTermIcon,
          },
        ],
      },
      {
        path: "",
        title: "Flexi Loan",
        activeIcon: ActiveFlexiIcon,
        inActiveIcon: InActiveFlexiIcon,
        subMenus: [
          {
            path: "/dashboard",
            title: "Loans",
            activeIcon: ActiveLoanFlexiIcon,
            inActiveIcon: InActiveLoanFlexiIcon,
          },
          {
            path: "",
            title: "Withdraw",
            activeIcon: ActiveWithdrawFlexiIcon,
            inActiveIcon: InActiveWithdrawFlexiIcon,
          },
          {
            path: "",
            title: "Payment",
            activeIcon: ActivePaymentsFlexiIcon,
            inActiveIcon: InActivePaymentsFlexiIcon,
          },
          {
            path: "",
            title: "Documents",
            activeIcon: ActiveDocumentFlexiIcon,
            inActiveIcon: InActiveDocumentFlexiIcon,
          },
        ],
      },
       {
        path: "",
        title: "Contact Us",
        activeIcon: ActiveApplicationIcon,
        inActiveIcon: InActiveApplicationIcon,
        subMenus: [
          {
            path: ROUTES.ContactUs.raiseRequest.locationPath,
            title: "Raise a Request",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
          {
            path: ROUTES.ContactUs.viewTickets.locationPath,
            title: "View Tickets",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
        ],
      },
      {
        path: "",
        title: "Quick Links",
        activeIcon: ActiveQuickLinksIcon,
        inActiveIcon: InActiveQuickLinksIcon,
        subMenus: [
          {
            path: "https://www.avanse.com/disclaimer",
            title: "Disclaimer",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
          {
            path: "https://www.avanse.com/privacy-policy",
            title: "Privacy Policy",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
          {
            path: "https://www.avanse.com/terms-conditions",
            title: "Terms & Conditions",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
          {
            path: "https://www.avanse.com/grievance-redressal-mechanism#ombudsman-scheme",
            title: "Ombudsman Scheme",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
          {
            path: "https://www.avanse.com/viewPagesAssets/pdf/grievance-redressal-mechanism-2022.pdf",
            title: "Customer Complaint",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
          {
            // In-app page, same as the desktop sidebar (QuickLinks in routerConfig). This
            // carried the Customer Complaint PDF URL above it — a copy-paste left over — so on
            // mobile it opened that PDF in a new tab instead of the Lodge a Complaint screen.
            // MobileDrawer sends anything starting with "http" to window.open and everything
            // else through navigate(), so a route path is all this needs.
            path: ROUTES.ContactUs.lodgeComplaint.locationPath,
            title: "Lodge a Complaint",
            activeIcon: ActiveApplicationIcon,
            inActiveIcon: InActiveApplicationIcon,
          },
        ],
      },
    ];

    const showTermLoan =
      termLoanData?.TermLoan?.length > 0 &&
      (businessType === "retail" || businessType === "entity" || tabType === "Term Loan");

    const showFlexiLoan =
      termLoanData?.Flexi?.length > 0 &&
      (businessType === "flexi" || tabType === "Flexi Term Loan");

    const filteredMenus = INTERNAL_MENUS.filter((menu) => {
      if (menu.title === "Term Loan") return showTermLoan;
      if (menu.title === "Flexi Loan") return showFlexiLoan;
      return true;
    });
    
    if (!showTermLoan && !showFlexiLoan) {
      const fallbackLoanMenu: MobileMenuItem = {
        title: "Loans",
        path: "/dashboard",
        activeIcon: ActiveLoanFlexiIcon, 
        inActiveIcon: InActiveLoanFlexiIcon,
      };
      filteredMenus.splice(1, 0, fallbackLoanMenu);
    }

    return filteredMenus;
  }, [tabType, businessType, termLoanData]);

  const handleMenuClick = () => {
    dispatch(setIsBypassLogin(false));
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    dispatch(setIsBypassLogin(false));
    setIsDrawerOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    dispatch(setIsBypassLogin(false));
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    dispatch(setIsBypassLogin(false));
    setAnchorEl(null);
  };

  const handleLogOutMenu = async () => {
    // Pre-login QRC session: just clear the QRC token/state and return to the Help Center.
    if (isQrcOnly) {
      handleProfileMenuClose();
      dispatch(clearQrcToken());
      window.location.href = ROUTES.HelpCenter.locationPath;
      return;
    }
    dispatch(setIsBypassLogin(false));
    if (
      selectedFormType?.formType === "continue" &&
      selectedFormType?.isNewData
    ) {
      dispatch(setDocumentDiscardModal());
    } else {
      try {
        window?.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "LOGOUT" })
        );
        await authServices.userLogout();
      } catch (error) {
        console.error("Logout API failed:", error);
      } finally {
        if (window.ReactNativeWebView?.postMessage) {
        console.log("chechchec");
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "LOGOUT" })
        );
        console.log("chechchec")
      }
        dispatch({ type: "USER_LOGOUT" });
        localStorage.removeItem("selectedIndex");
        handleProfileMenuClose();
        sessionStorage?.clear();
        await persistor.purge();
        window.location.href = "/login";
      }
    }
  };

  return (
    <Box
      sx={
        isXsOrSm ? { position: "absolute", top: 0, left: 0, width: "100%" } : { width: "100%" }
      }
    >
      {/* Pre-login QRC visitors get no welcome bar — the redesigned Help Center / Contact Us
          screens carry their own hero, and logout lives on that hero (see PreLoginHero). */}
      {isAuthenticated && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: isXsOrSm ? "80px" : "90px",
            p: { xs: 1, md: 3 },
            boxShadow: "0px 2px 4px 0px #A5A3AE4D",
            borderRadius: isXsOrSm ? 0 : "6px",
            backgroundColor: "#FFFFFF99",
            marginBottom: "15px",
            backdropFilter: "blur(10px)",
          }}
        >
          {/* Left Section: Menu Icon and Welcome Message */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isXsOrSm && isAuthenticated && (
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: isXsOrSm ? 1 : 2 }}
                onClick={handleMenuClick}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box>
              {/* <TruncatedTextWithTooltip
                text={`Welcome back ${userName}\u{1F44B}`}
                limit={isXsOrSm ? 20 : 30}
                style={{
                  fontWeight: 600,
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: "30px",
                  color: "#27575C",
                }}
              /> */}
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: "30px",
                  color: "#27575C",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "default",
                }}
              >
                {isAuthenticated ? `Welcome back ${userName}\u{1F44B}` : "Customer Support"}
              </Typography>
              {!isXsOrSm && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: 12,
                    lineHeight: "22px",
                    color: "#7d9093ff",
                  }}
                >
                  {isAuthenticated
                    ? "Monitor all your loan applications and loan accounts from here"
                    : "Raise and track your service requests here"}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right Section: Notification and Avatar */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* <IconButton size="large" aria-label="show notifications">
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton> */}

            <IconButton
              sx={{
                paddingLeft: "15px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
              size="large"
              aria-label="account of current user"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
            >
              {isQrcOnly ? (
                <Box
                  sx={{
                    width: isXsOrSm ? 30 : 40,
                    height: isXsOrSm ? 30 : 40,
                    borderRadius: "50%",
                    bgcolor: "#1CB1B1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonOutlineIcon sx={{ color: "#fff", fontSize: isXsOrSm ? 18 : 22 }} />
                </Box>
              ) : (
                <ProfileAvatar
                  name={avatarSrc || userProfile?.name}
                  src={userProfile?.profileImage}
                  size={isXsOrSm ? 30 : 40}
                />
              )}
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={isProfileMenuOpen}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {!isQrcOnly && (
              <MenuItem
                onClick={() => {
                  navigate("/profile");
                  handleProfileMenuClose();
                }}
              >
                <img src={userd} alt="user" style={{ marginRight: 8 }} /> Profile
              </MenuItem>
            )}
            <MenuItem onClick={handleLogOutMenu}>
              <img src={logout} alt="log out" style={{ marginRight: 8 }} />{" "}
              Logout
            </MenuItem>
          </Menu>
        </Box>
      )}

      {isXsOrSm && (
        <MobileDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          handleLogOutMenu={handleLogOutMenu}
          menuItems={mobileMenuItems} 
        />
      )}
    </Box>
  );
};

const Header = () => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ width: "100%" }}>
      {isXsOrSm ? (
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#5d586c",
              fontSize: "18px",
              lineHeight: "1.5",
              mb: 2,
              mt: 2,
              px: 1
            }}
          >
            Hi! You are one step closer to your dream University - lets check your
            eligibility! 👋
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0px 2px 4px 0px #A5A3AE4D",
            background: "#fff",
            padding: "14px 11px",
            borderRadius: "6px",
            gap: 2,
          }}
          mb={2}
        >
          {/* Left Side: Text */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isXsOrSm ? "body1" : "h6"}
              sx={{
                fontWeight: 600,
                color: "#27575C",
                fontSize: isXsOrSm ? "14px" : "inherit",
                lineHeight: isXsOrSm ? "1.4" : "inherit",
              }}
            >
              Hi! You are one step closer to your dream University - lets check your
              eligibility! 👋
            </Typography>
          </Box>

          {/* Right Side: Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            <img
              src={CompanyLogo}
              alt="Company Logo"
              style={{
                height: isXsOrSm ? 32 : 40,
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Header;